import type {Item} from '../lib/react-use-cart'

/**
 * The part of a cart that matters when syncing between tabs.
 *
 * Deliberately ignores everything derived — `itemTotal`, `cartTotal`, the
 * provider's random cart id — because two tabs holding the same cart still
 * serialize those differently, and a difference there is not a reason to
 * adopt anything.
 */
export function cart_signature(items: Item[] | null | undefined): string {
  return (items ?? [])
    .map((item) => `${item.id}:${item.quantity ?? 1}:${item.price}`)
    .join('|')
}

/**
 * Should a cart arriving on a `storage` event replace the one we hold?
 *
 * Only when it actually differs. Adopting an identical cart is not merely
 * wasted work: `setItems` POSTs to /api/cart and writes the cart back to
 * localStorage, which raises a `storage` event in the tab we just heard from,
 * which adopts ours, which writes back… With two tabs open that volley ran at
 * browser speed — one real Chrome produced 104,535 rejected /api/cart calls in
 * 96 seconds, and `refresh` (the call `setItems` makes) outnumbered `reserve`
 * about 90:1 in the same window. 1.18M rejections in a week, every one on this
 * route, from 48 IPs that were all genuine browsers.
 *
 * The endpoint's per-IP rate limit was the only thing containing it, which is
 * why raising or re-keying that limit would have made things worse rather than
 * better.
 */
export function should_adopt_cart(
  incoming: Item[] | null | undefined,
  current: Item[] | null | undefined,
): boolean {
  return cart_signature(incoming) !== cart_signature(current)
}
